export const RENDERING_TABS = {
  customer: 'customer',
  fqdn: 'fqdn',
  url: 'url',
};

/**
 * Boot-time pair only, same codes as RenderingConfigService. After the first
 * key_policy response, use key_policy.fallback_engines.
 */
export const FALLBACK_HTML_ENGINE = 'curl_only';
export const FALLBACK_SCREENSHOT_ENGINE = 'phantomjs_screenshot_v2_1';

/**
 * Mirrors RenderingConfigValidator allowedKeys/validateValue so each key gets the right
 * input. The API still validates; a mismatch here shows as a 422, never as a silent write.
 *
 * Privileged path and proxy values are plain text fields. Do not put internal hosts or
 * filesystem paths in this file — they ship in the public JS bundle. Public vs staff comes
 * from API key_policy.customer_keys, not from flags on these field defs.
 */
const FIELDS = {
  html_config: [
    { key: 'screenWidth', label: 'Width', type: 'number', min: 1, max: 10000, sharedOnly: true },
    { key: 'screenHeight', label: 'Height', type: 'number', min: 1, max: 10000, sharedOnly: true },
    { key: 'timeout', label: 'Timeout (ms)', type: 'number', min: 1000, max: 120000 },
    { key: 'additionalDelay', label: 'Additional delay (ms)', type: 'number', min: 0, max: 120000 },
    { key: 'selectorTimeout', label: 'Selector timeout (ms)', type: 'number', min: 0, max: 120000, sharedOnly: true },
    { key: 'clientRedirectWaitTimeout', label: 'Client redirect wait (ms)', type: 'number', min: 0, max: 120000 },
    { key: 'selectorIds', label: 'Selector IDs', type: 'list', hint: 'One per line, up to 20', sharedOnly: true },
    { key: 'redirectWhitelist', label: 'Redirect whitelist', type: 'list', hint: 'One per line, up to 25' },
    {
      key: 'videoPauseWhitelist',
      label: 'Video pause whitelist',
      type: 'list',
      hint: 'Child-frame URL prefixes, one per line, up to 25',
    },
    {
      key: 'blockedHosts',
      label: 'Blocked hosts',
      type: 'list',
      hint: 'One hostname per line, up to 50',
    },
    { key: 'hasAnimation', label: 'Has animation', type: 'boolean' },
    { key: 'pauseVideos', label: 'Pause videos', type: 'boolean' },
    {
      key: 'replaceInstagramEmbeds',
      label: 'Replace Instagram embeds',
      type: 'boolean',
      sharedOnly: true,
    },
    {
      key: 'htmlRenderMode',
      label: 'HTML render mode',
      type: 'select',
      options: ['datapipes', 'ns_gov_retry', 'lambda_fail_abort'],
    },
  ],
  screenshot_config: [
    { key: 'screenWidth', label: 'Width', type: 'number', min: 1, max: 10000 },
    { key: 'screenHeight', label: 'Height', type: 'number', min: 1, max: 10000 },
    { key: 'timeout', label: 'Timeout (ms)', type: 'number', min: 1000, max: 120000 },
    { key: 'additionalDelay', label: 'Additional delay (ms)', type: 'number', min: 0, max: 120000 },
    { key: 'selectorTimeout', label: 'Selector timeout (ms)', type: 'number', min: 0, max: 120000, sharedOnly: true },
    { key: 'clientRedirectWaitTimeout', label: 'Client redirect wait (ms)', type: 'number', min: 0, max: 120000 },
    { key: 'selectorIds', label: 'Selector IDs', type: 'list', hint: 'One per line, up to 20', sharedOnly: true },
    { key: 'redirectWhitelist', label: 'Redirect whitelist', type: 'list', hint: 'One per line, up to 25' },
    {
      key: 'videoPauseWhitelist',
      label: 'Video pause whitelist',
      type: 'list',
      hint: 'Child-frame URL prefixes, one per line, up to 25',
    },
    {
      key: 'blockedHosts',
      label: 'Blocked hosts',
      type: 'list',
      hint: 'One hostname per line, up to 50',
    },
    {
      key: 'waitUntil',
      label: 'Wait until',
      type: 'select',
      options: ['load', 'domcontentloaded', 'networkidle0', 'networkidle2'],
    },
    { key: 'autoscroll', label: 'Autoscroll', type: 'boolean' },
    { key: 'hasScrollRestriction', label: 'Scroll restriction', type: 'boolean' },
    { key: 'hasAnimation', label: 'Has animation', type: 'boolean' },
    { key: 'pauseVideos', label: 'Pause videos', type: 'boolean' },
    {
      key: 'replaceInstagramEmbeds',
      label: 'Replace Instagram embeds',
      type: 'boolean',
      sharedOnly: true,
    },
    { key: 'useChrome', label: 'Use Chrome', type: 'boolean' },
    { key: 'userAgent', label: 'User agent', type: 'userAgent' },
    { key: 'loadUaFromDb', label: 'Load UA from DB', type: 'boolean' },
    {
      key: 'proxy',
      label: 'Proxy',
      type: 'text',
      hint: 'host:port. The API allowlist rejects unknown values.',
    },
    {
      key: 'script',
      label: 'Script',
      type: 'text',
      hint: 'Absolute path. The API allowlist rejects unknown values.',
    },
    {
      key: 'binary',
      label: 'Binary',
      type: 'text',
      hint: 'Absolute path. The API allowlist rejects unknown values.',
    },
  ],
};

/**
 * Staff-only curl transport keys. Stored in the curlConfig column, not htmlConfig.
 * Mirrors RenderingConfigValidator curlConfig allowlist (timeout stays on html/screenshot).
 */
const CURL_FIELDS = [
  {
    key: 'curlProxy',
    label: 'Curl proxy',
    type: 'text',
    hint: 'Full URL. The API allowlist rejects unknown values.',
  },
  { key: 'disableKeepalive', label: 'Disable keep-alive', type: 'boolean' },
  {
    key: 'curlResolve',
    label: 'Curl resolve',
    type: 'list',
    hint: 'One host:port:ip per line',
  },
  {
    key: 'curlResolveElb',
    label: 'Curl resolve ELB',
    type: 'text',
  },
  {
    key: 'sslVerifyHost',
    label: 'SSL verify host',
    type: 'select',
    options: [0, 2],
  },
  { key: 'useCookieFile', label: 'Use cookie file', type: 'boolean' },
  {
    key: 'curlHeaders',
    label: 'Curl headers',
    type: 'list',
    hint: 'One header per line',
  },
  {
    key: 'curlEncoding',
    label: 'Curl encoding',
    type: 'select',
    options: ['', 'identity', 'gzip', 'deflate', 'br', 'gzip, deflate', 'gzip, deflate, br'],
  },
  {
    key: 'curlHttpVersion',
    label: 'Curl HTTP version',
    type: 'select',
    options: [0, 1, 2, 3],
  },
  { key: 'emptyRetry', label: 'Retry on empty response', type: 'boolean' },
];

/**
 * Engine catalog fields, grouped for the admin editor. Mirrors
 * RenderingConfigValidator::allowedKeys('engine'), so the editor offers only keys the API
 * accepts. Transport keys route the request; default keys seed every layer below.
 */
export const ENGINE_FIELD_GROUPS = [
  {
    name: 'transport',
    title: 'Transport',
    hint: 'How the worker reaches this engine.',
    fields: [
      { key: 'endpointUrl', label: 'Endpoint URL', type: 'text' },
      { key: 'region', label: 'AWS region', type: 'text' },
      { key: 'functionName', label: 'Lambda function', type: 'text' },
      { key: 'functionNameChrome', label: 'Lambda function (Chrome)', type: 'text' },
      { key: 'functionNameAnimation', label: 'Lambda function (animation)', type: 'text' },
      { key: 'bucketName', label: 'S3 bucket', type: 'text' },
      { key: 'pathPrefix', label: 'S3 path prefix', type: 'text' },
      {
        key: 'script',
        label: 'Script',
        type: 'text',
        hint: 'Absolute path. The API allowlist rejects unknown values.',
      },
      {
        key: 'binary',
        label: 'Binary',
        type: 'text',
        hint: 'Absolute path. The API allowlist rejects unknown values.',
      },
      { key: 'cacheWindowSeconds', label: 'Cache window (s)', type: 'number', min: 0, max: 86400 },
    ],
  },
  {
    name: 'defaults',
    title: 'Default values',
    hint: 'Applied when no customer, FQDN, or URL layer sets the key.',
    fields: [
      { key: 'screenWidth', label: 'Width', type: 'number', min: 1, max: 10000 },
      { key: 'screenHeight', label: 'Height', type: 'number', min: 1, max: 10000 },
      { key: 'timeout', label: 'Timeout (ms)', type: 'number', min: 1000, max: 120000 },
      { key: 'additionalDelay', label: 'Additional delay (ms)', type: 'number', min: 0, max: 120000 },
      { key: 'selectorTimeout', label: 'Selector timeout (ms)', type: 'number', min: 0, max: 120000 },
      {
        key: 'clientRedirectWaitTimeout',
        label: 'Client redirect wait (ms)',
        type: 'number',
        min: 0,
        max: 120000,
      },
      {
        key: 'waitUntil',
        label: 'Wait until',
        type: 'select',
        options: ['load', 'domcontentloaded', 'networkidle0', 'networkidle2'],
      },
      { key: 'userAgent', label: 'User agent', type: 'userAgent' },
      { key: 'selectorIds', label: 'Selector IDs', type: 'list', hint: 'One per line, up to 20' },
      {
        key: 'redirectWhitelist',
        label: 'Redirect whitelist',
        type: 'list',
        hint: 'One per line, up to 25',
      },
      {
        key: 'videoPauseWhitelist',
        label: 'Video pause whitelist',
        type: 'list',
        hint: 'Child-frame URL prefixes, one per line, up to 25',
      },
      {
        key: 'blockedHosts',
        label: 'Blocked hosts',
        type: 'list',
        hint: 'One hostname per line, up to 50',
      },
      { key: 'autoscroll', label: 'Autoscroll', type: 'boolean' },
      { key: 'hasAnimation', label: 'Has animation', type: 'boolean' },
      { key: 'pauseVideos', label: 'Pause videos', type: 'boolean' },
      { key: 'hasScrollRestriction', label: 'Scroll restriction', type: 'boolean' },
      { key: 'replaceInstagramEmbeds', label: 'Replace Instagram embeds', type: 'boolean' },
      { key: 'useChrome', label: 'Use Chrome', type: 'boolean' },
      { key: 'ignoreSslErrors', label: 'Ignore SSL errors', type: 'boolean' },
      {
        key: 'sslProtocol',
        label: 'SSL protocol',
        type: 'select',
        options: ['any', 'sslv2', 'sslv3', 'tlsv1', 'tlsv1.0', 'tlsv1.1', 'tlsv1.2', 'tlsv1.3'],
      },
      {
        key: 'proxy',
        label: 'Proxy',
        type: 'text',
        hint: 'host:port. The API allowlist rejects unknown values.',
      },
    ],
  },
];

export const ENGINE_KINDS = ['html', 'screenshot', 'both'];

/** One engine field by key, or null when the key is outside the catalog allowlist. */
export function engineFieldFor(key) {
  let found = null;
  ENGINE_FIELD_GROUPS.forEach((group) => {
    group.fields.forEach((field) => {
      if (field.key === key) {
        found = field;
      }
    });
  });
  return found;
}

export const CONFIG_SECTIONS = [
  { name: 'html_config', title: 'HTML config' },
  { name: 'screenshot_config', title: 'Screenshot config' },
];

/**
 * A sharedOnly field reaches the renderer only through the shared HTML + screenshot
 * pipeline, so no other engine reads it.
 *
 * supportedKeys holds the layer keys the effective engine reads. The API sends that list
 * (resolve.layer_keys, or layer_keys on an engine row), so the frontend keeps no customer
 * or engine key map of its own. A missing list means the supported keys are unknown, for
 * example while resolve still loads: show no fields, not every field.
 */
export function fieldsFor(configName, shared = false, supportedKeys = null) {
  return (FIELDS[configName] || []).filter(
    (field) =>
      Array.isArray(supportedKeys)
      && supportedKeys.includes(field.key)
      && (shared || !field.sharedOnly)
  );
}

/** The single field list shown when one engine renders both outputs. */
export function sharedFieldsFor(supportedKeys = null) {
  return fieldsFor('screenshot_config', true, supportedKeys);
}

/** Staff-only curl transport fields for the dedicated curlConfig panel. */
export function curlFields() {
  return CURL_FIELDS.slice();
}

/** Routing keys the worker strips from engine defaults before it builds a request config. */
const ENGINE_METADATA_KEYS = [
  'endpointUrl',
  'region',
  'functionName',
  'functionNameChrome',
  'functionNameAnimation',
  'bucketName',
  'script',
  'binary',
  'pathPrefix',
  'cacheWindowSeconds',
];

/**
 * Mirrors RenderingConfigService::engineRequestDefaults(), so the form can show the
 * defaults of a newly picked engine before the change is saved and re-resolved.
 */
export function engineRequestDefaults(engine) {
  const config = (engine && engine.default_config) || {};
  const out = {};
  Object.keys(config).forEach((key) => {
    if (ENGINE_METADATA_KEYS.indexOf(key) !== -1) {
      return;
    }
    if (config[key] === null || config[key] === undefined) {
      return;
    }
    out[key] = config[key];
  });
  return out;
}

/** A shared key is written to every config blob whose allowlist accepts it. */
export function configsAcceptingKey(key) {
  return CONFIG_SECTIONS.map((section) => section.name).filter((name) =>
    (FIELDS[name] || []).some((field) => field.key === key)
  );
}

/** Config values -> UI values. An absent key stays empty. */
export function configToForm(config, fields) {
  const form = {};
  fields.forEach((field) => {
    const has = config && Object.prototype.hasOwnProperty.call(config, field.key);
    const raw = has ? config[field.key] : null;
    if (!has || raw === null || raw === undefined) {
      form[field.key] = field.type === 'boolean' || field.type === 'select' ? undefined : null;
      if (field.type === 'text' || field.type === 'list' || field.type === 'userAgent') {
        form[field.key] = '';
      }
      return;
    }
    if (field.type === 'boolean') {
      const normalized = typeof raw === 'string' ? raw.trim().toLowerCase() : raw;
      const trueValues = [true, 1, '1', 'true', 'yes', 'on'];
      const falseValues = [false, 0, '0', 'false', 'no', 'off'];
      if (trueValues.includes(normalized)) {
        form[field.key] = 'true';
      } else if (falseValues.includes(normalized)) {
        form[field.key] = 'false';
      } else {
        // Do not turn an unknown legacy value into false during a routine save.
        form[field.key] = undefined;
      }
    } else if (field.type === 'list') {
      form[field.key] = Array.isArray(raw) ? raw.join('\n') : String(raw);
    } else if (field.type === 'number') {
      form[field.key] = Number(raw);
    } else {
      form[field.key] = raw;
    }
  });
  return form;
}

/** UI values -> config values. Empty fields are omitted, so the parent value applies. */
export function formToConfig(form, fields) {
  const config = {};
  fields.forEach((field) => {
    const value = form ? form[field.key] : null;
    if (value === null || value === undefined || value === '') {
      return;
    }
    if (field.type === 'boolean') {
      config[field.key] = value === 'true' || value === true;
    } else if (field.type === 'list') {
      const items = String(value)
        .split('\n')
        .map((item) => item.trim())
        .filter((item) => item !== '');
      if (items.length) {
        config[field.key] = items;
      }
    } else if (field.type === 'number') {
      const num = Number(value);
      if (!Number.isNaN(num)) {
        config[field.key] = num;
      }
    } else {
      config[field.key] = value;
    }
  });
  return config;
}

export function emptyConfigPair() {
  return { html_config: {}, screenshot_config: {}, curl_config: {} };
}

/**
 * Options from tblUserAgent. If the current value is a custom string that is not
 * in the catalog, keep it as an extra option so the select does not go blank.
 */
export function buildUserAgentOptions(userAgents, currentValue) {
  const options = (userAgents || [])
    .map((agent) => {
      const value = agent.user_agent_string || agent.uaUserAgent || '';
      if (!value) {
        return null;
      }
      const alias = agent.alias || agent.uaAlias || '';
      return {
        value,
        label: alias || value,
      };
    })
    .filter(Boolean);
  if (currentValue && !options.some((option) => option.value === currentValue)) {
    options.unshift({ value: currentValue, label: currentValue });
  }
  return options;
}

export function getApiErrorMessage(error, fallback = 'Request failed.') {
  const data = error && error.response && error.response.data;
  if (!data) {
    return fallback;
  }
  if (typeof data.message === 'string' && data.message) {
    return data.message;
  }
  if (data.errors && typeof data.errors === 'object') {
    const firstKey = Object.keys(data.errors)[0];
    const first = firstKey && data.errors[firstKey];
    if (Array.isArray(first) && first[0]) {
      return first[0];
    }
    if (typeof first === 'string') {
      return first;
    }
  }
  return fallback;
}
